import { Navigation } from '@/components/Navigation';
import { BackgroundPolaroids } from '@/components/BackgroundPolaroids';
import { Footer } from '@/components/Footer';
import { ManifestSection } from '@/components/ManifestSection';
import { ValuesSection } from '@/components/ValuesSection';
import { WhyNowSection } from '@/components/WhyNowSection';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { SEO } from '@/components/SEO';
import piotrPortrait from '@/assets/piotr-portrait.jpg';
const AboutMysticForest = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleBackToHome = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('about');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <>
    <SEO
      title="About Mystic Forest - Our Philosophy & Vision"
      description="Discover the philosophy, values, and vision behind Mystic Forest. Learn about our mission to create analog experiences and mindful retreats in nature."
      canonicalUrl="https://mysticforest.club/about-mystic-forest"
    />
    
    <div className="min-h-screen bg-background relative">
      <BackgroundPolaroids />
      
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button onClick={handleBackToHome} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </div>

      <div className="relative z-20">
        <Navigation />
        
        {/* Hero Section */}
        <section className="py-24 bg-background">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <h1 className="text-5xl md:text-7xl font-serif font-light mb-8 text-foreground leading-tight">The Spirit of Mystic Forest</h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Discover the philosophy, values, and vision behind our analogue experience club
            </p>
          </div>
        </section>

        {/* Content Sections */}
        <ManifestSection />
        <ValuesSection />
        <WhyNowSection />
        
        {/* Our Relationship with Technology Section */}
        <section id="technology" className="py-24 bg-background">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif font-light mb-8 text-foreground leading-tight">
                Our Relationship with Technology
              </h2>
              <div className="w-24 h-px bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mb-8"></div>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                We're not anti-technology or anti-AI — in fact, we use them every day. <span className="relative inline-block px-2 py-1 font-semibold bg-lime-400/60 -rotate-1 rounded-sm">What matters is the quality of our relationship with technology</span>. For us, that means tech should serve, not drain. It's about knowing when to unplug, recharge, and return in balance — enjoying the benefits without losing ourselves along the way.
              </p>
            </div>
          </div>
        </section>
        
        {/* About Us Section */}
        <section id="about" className="py-24 bg-card relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="absolute top-20 right-20 w-32 h-32 rounded-full border border-accent animate-pulse" />
            <div className="absolute bottom-20 left-20 w-24 h-24 rounded-full border border-accent animate-pulse delay-700" />
            <div className="absolute top-1/2 left-1/4 w-16 h-16 rounded-full border border-accent animate-pulse delay-1000" />
          </div>

          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif font-light mb-8 text-foreground leading-tight">
                My Journey into the Forest
              </h2>
              <div className="w-24 h-px bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mb-8"></div>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Meet the people behind Mystic Forest and discover the personal stories that inspire our mission
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              {/* Piotr's Story */}
              <div className="bg-background/60 backdrop-blur-sm border border-border/20 rounded-3xl p-10 md:p-12 shadow-2xl">
              <div>
                  <div className="text-base leading-relaxed text-muted-foreground space-y-6 max-w-4xl mx-auto">
                    <div className="space-y-4">
                      <p>
                        I'm Piotr — the person behind Mystic Forest. This idea has been quietly growing inside me for years before it finally found its name.
                      </p>
                      
                      <p>
                        For nearly two decades, I lived like many of us do — working hard, chasing deadlines, constantly connected, always "on." Somewhere along the way, I realized I'd forgotten how to truly rest.
                      </p>
                      
                      <p>
                        Then life, as it sometimes does, pressed pause. A few difficult moments made me slow down and ask some overdue questions: Is this the life I really want? And, more importantly — is this the life I want my kids to see me living?
                      </p>
                      
                      <p>
                        As a father of three, I knew I didn't want to teach them that being busy is a badge of honor. I wanted them to know that joy can live offline too — in the forest, around a table, under the stars.
                      </p>
                      
                      <p>
                        Mystic Forest grew out of that reflection. It's my way of finding balance again — of returning to the kind of simplicity I remember from my childhood in the 80s. Less noise, more presence.
                      </p>
                      
                      <p>
                        But this isn't just my story. It's an open invitation — for anyone who feels the same pull to slow down, reconnect, and make space for what really matters.
                      </p>
                      
                      <p className="font-semibold text-foreground text-lg">
                        Let's walk that path together.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  </>;
};
export default AboutMysticForest;